import axios from 'axios';
import { API_URL } from '../config/apiConfig';
import { extractCityAndState } from '../utils/locationMatcher';


/**
 * Check if a fixed route exists between two cities
 * @param {string} fromCityId - Origin city ID
 * @param {string} toCityId - Destination city ID
 * @returns {Promise} - Promise resolving to fixed route data or null
 */
export const checkFixedRoute = async (fromCityId, toCityId) => {
  try {
    const response = await axios.post(`${API_URL}/fixed-routes/check`, {
      fromCityId,
      toCityId
    });
    
    if (response.data.success && response.data.isFixedRoute) {
      return response.data.data;
    }
    
    return null;
  } catch (error) {
    console.error('Error checking for fixed route:', error);
    return null;
  }
};

/**
 * Get all fixed routes (for admin panel)
 * @returns {Promise} - Promise resolving to array of fixed routes
 */
export const getAllFixedRoutes = async () => {
  try {
    // First try the fixed-routes endpoint
    try {
      const response = await axios.get(`${API_URL}/fixed-routes`);
      if (response.data.success) {
        return response.data.data;
      }
    } catch (routeError) {
      // API endpoint not available - this is expected on first run
    }
    
    // Return empty array if no data or endpoint not available
    return [];
  } catch (error) {
    console.error('Error fetching fixed routes:', error);
    return [];
  }
};

/**
 * Create a new fixed route (admin only)
 * @param {Object} routeData - Fixed route data
 * @param {string} token - Admin auth token
 * @returns {Promise} - Promise resolving to created route data
 */
export const createFixedRoute = async (routeData, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    
    const response = await axios.post(`${API_URL}/fixed-routes`, routeData, config);
    
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create fixed route');
  }
};

/**
 * Update an existing fixed route (admin only)
 * @param {string} id - Fixed route ID
 * @param {Object} routeData - Updated route data
 * @param {string} token - Admin auth token
 * @returns {Promise} - Promise resolving to updated route data
 */
export const updateFixedRoute = async (id, routeData, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    
    const response = await axios.put(`${API_URL}/fixed-routes/${id}`, routeData, config);
    
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update fixed route');
  }
};

/**
 * Delete a fixed route (admin only)
 * @param {string} id - Fixed route ID
 * @param {string} token - Admin auth token
 * @returns {Promise} - Promise resolving to success message
 */
export const deleteFixedRoute = async (id, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };

    const response = await axios.delete(`${API_URL}/fixed-routes/${id}`, config);

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to delete fixed route');
  }
};

/**
 * Check if a popular route exists between two cities
 * Supports city and state matching
 * @param {string} from - Origin location (can include state/country)
 * @param {string} to - Destination location (can include state/country)
 * @returns {Promise} - Promise resolving to popular route data or null
 */
export const checkPopularRoute = async (from, to) => {
  try {
    // Extract city names from full addresses using smart extraction
    const { city: cleanFromCity } = extractCityAndState(from);
    const { city: cleanToCity } = extractCityAndState(to);

    console.log("checkPopularRoute - Searching for route:", cleanFromCity, "->", cleanToCity);

    const response = await axios.get(`${API_URL}/popular-routes/find`, {
      params: {
        from: cleanFromCity,
        to: cleanToCity
      }
    });

    console.log("checkPopularRoute - API response:", response.data);

    if (response.data && response.data._id) {
      console.log("checkPopularRoute - Found popular route, returning data");
      return response.data;
    }

    console.log("checkPopularRoute - No valid route data returned");
    return null;
  } catch (error) {
    // Route not found is normal, return null silently
    if (error.response?.status === 404) {
      console.log("checkPopularRoute - Route not found (404)");
      return null;
    }
    console.error('Error checking for popular route:', error);
    return null;
  }
};

/**
 * Get all active popular routes (for homepage display)
 * @returns {Promise} - Promise resolving to array of popular routes
 */
export const getPopularRoutes = async () => {
  try {
    const response = await axios.get(`${API_URL}/popular-routes`);

    if (response.data) {
      return Array.isArray(response.data) ? response.data : response.data.data || [];
    }

    return [];
  } catch (error) {
    console.error('Error fetching popular routes:', error);
    return [];
  }
};
